import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Descriptions,
  Table,
  Tag,
  Button,
  Space,
  Divider,
  Typography,
  Card,
  Row,
  Col,
  Statistic,
  Modal,
  Form,
  Select,
  Spin,
} from 'antd';
import {
  CheckOutlined,
  DownloadOutlined,
  TruckOutlined,
} from '@ant-design/icons';
import { hasPermission } from '../../utils/permissions';
import {
  fetchPODeliveryStatusRequest,
  fetchDeliveryChallanRequest,
  updateDeliveryChallanRequest,
} from '../../redux/actions/deliveryChallanActions';

const { Title, Text } = Typography;
const { Option } = Select;

const getStatusTag = (status) => {
  const map = {
    pending: { color: 'orange', text: 'Pending' },
    in_transit: { color: 'blue', text: 'In Transit' },
    partially_delivered: { color: 'purple', text: 'Partially Delivered' },
    delivered: { color: 'green', text: 'Delivered' },
    cancelled: { color: 'red', text: 'Cancelled' },
    received: { color: 'cyan', text: 'Received' },
    verified: { color: 'green', text: 'Verified' },
    rejected: { color: 'red', text: 'Rejected' },
    partial: { color: 'orange', text: 'Partial' },
    accepted: { color: 'green', text: 'Accepted' },
    damaged: { color: 'red', text: 'Damaged' },
  };
  const config = map[status] || { color: 'default', text: status || '-' };
  return <Tag color={config.color}>{config.text}</Tag>;
};

const hasValue = (value) => value !== undefined && value !== null && value !== '';

const toSafeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const roundMoney = (value) =>
  Math.round((toSafeNumber(value) + Number.EPSILON) * 100) / 100;

const formatCurrency = (value) =>
  `₹${toSafeNumber(value).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const getFirstNumericValue = (source, keys, fallback = 0) => {
  for (const key of keys) {
    const rawValue = source?.[key];
    if (!hasValue(rawValue)) continue;

    const parsed = Number(rawValue);
    if (Number.isFinite(parsed)) return parsed;
  }

  return fallback;
};

const getAcceptedQuantity = (item) => {
  const deliveredQuantity = Math.max(
    0,
    getFirstNumericValue(item, ['delivered_quantity', 'delivering_quantity'])
  );
  const rejectedQuantity = Math.max(
    0,
    getFirstNumericValue(item, ['rejected_quantity'])
  );
  const fallback = Math.max(0, deliveredQuantity - rejectedQuantity);

  if (hasValue(item?.accepted_quantity)) {
    const stored = Math.max(0, toSafeNumber(item.accepted_quantity));
    // A stored 0 alongside a clearly-positive delivered-minus-rejected total
    // means accepted_quantity was never actually recorded for this item
    // (e.g. an older challan created before item-level accepted tracking, or
    // via the simple record-delivery path) rather than a genuine "nothing
    // accepted" outcome - fall back to delivered - rejected in that case.
    return stored > 0 || fallback === 0 ? stored : fallback;
  }

  return fallback;
};

const computeItemFinancials = (item, selectedQuantity) => {
  const quantity = Math.max(0, toSafeNumber(selectedQuantity));
  const orderedQuantity = Math.max(
    0,
    getFirstNumericValue(item, ['ordered_quantity', 'quantity'])
  );
  const rate = Math.max(
    0,
    getFirstNumericValue(item, ['rate', 'purchase_rate', 'unit_rate', 'billed_rate'])
  );

  const discountPercentage = Math.max(
    0,
    getFirstNumericValue(item, ['discount', 'discount_percentage', 'discount_percent'])
  );
  const taxPercentage = Math.max(
    0,
    getFirstNumericValue(item, ['tax_slab', 'tax_percentage', 'gst_percentage'])
  );

  const subtotalAmount = quantity * rate;

  const discountAmountFromLine =
    orderedQuantity > 0
      ? (Math.max(0, getFirstNumericValue(item, ['discount_amount'])) / orderedQuantity) *
        quantity
      : 0;

  const discountAmount =
    discountPercentage > 0
      ? (subtotalAmount * discountPercentage) / 100
      : discountAmountFromLine;

  const taxableAmount = Math.max(0, subtotalAmount - discountAmount);

  const taxAmountFromLine =
    orderedQuantity > 0
      ? (Math.max(0, getFirstNumericValue(item, ['tax_amount'])) / orderedQuantity) * quantity
      : 0;

  const taxAmount =
    taxPercentage > 0 ? (taxableAmount * taxPercentage) / 100 : taxAmountFromLine;

  const finalAmount = taxableAmount + taxAmount;

  return {
    subtotal_amount: roundMoney(subtotalAmount),
    discount_amount: roundMoney(discountAmount),
    taxable_amount: roundMoney(taxableAmount),
    tax_amount: roundMoney(taxAmount),
    final_amount: roundMoney(finalAmount),
  };
};

const computeFinancialSummaryFromItems = (items = []) => {
  const totals = items.reduce(
    (acc, item) => {
      const acceptedQuantity = getAcceptedQuantity(item);

      if (acceptedQuantity <= 0) {
        return acc;
      }

      const computed = computeItemFinancials(item, acceptedQuantity);

      return {
        accepted_quantity: acc.accepted_quantity + acceptedQuantity,
        subtotal_amount: acc.subtotal_amount + computed.subtotal_amount,
        discount_amount: acc.discount_amount + computed.discount_amount,
        taxable_amount: acc.taxable_amount + computed.taxable_amount,
        tax_amount: acc.tax_amount + computed.tax_amount,
        final_amount: acc.final_amount + computed.final_amount,
      };
    },
    {
      accepted_quantity: 0,
      subtotal_amount: 0,
      discount_amount: 0,
      taxable_amount: 0,
      tax_amount: 0,
      final_amount: 0,
    }
  );

  return {
    accepted_quantity: roundMoney(totals.accepted_quantity),
    subtotal_amount: roundMoney(totals.subtotal_amount),
    discount_amount: roundMoney(totals.discount_amount),
    taxable_amount: roundMoney(totals.taxable_amount),
    tax_amount: roundMoney(totals.tax_amount),
    final_amount: roundMoney(totals.final_amount),
  };
};

const DeliveryChallanDetail = ({ challan, onClose }) => {
  const dispatch = useDispatch();
  const [updateForm] = Form.useForm();
  const permissions = useSelector((state) => state.auth.permissions || []);
  const { deliveryStatus, currentChallan, loading } = useSelector(
    (state) => state.deliveryChallans
  );

  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);

  const canUpdate = hasPermission(permissions, 'delivery_challan/update');
  const canView = hasPermission(permissions, 'delivery_challan/view');

  // Fetch full challan by ID (GET /api/delivery-challans/:id) so that
  // item-level data is always present even if the list response omits it.
  useEffect(() => {
    if (challan?.id && canView) {
      dispatch(fetchDeliveryChallanRequest(challan.id));
    }
  }, [challan?.id, canView, dispatch]);

  // Fetch PO delivery status to show all associated challans for the PO
  useEffect(() => {
    if (challan?.purchase_order_id && canView) {
      dispatch(fetchPODeliveryStatusRequest(challan.purchase_order_id));
    }
  }, [challan?.purchase_order_id, canView, dispatch]);

  // Use the fully-loaded challan from Redux state once available; fall back to
  // the row data passed as prop while the fetch is in-flight.
  const detail =
    currentChallan?.id === challan?.id ? currentChallan : challan;

  const financialSummary = useMemo(() => {
    const itemsSummary = computeFinancialSummaryFromItems(detail?.items || []);

    const hasStoredBreakdown = [
      'subtotal_amount',
      'discount_amount',
      'taxable_amount',
      'tax_amount',
      'final_amount',
      'total_amount',
    ].some((field) => hasValue(detail?.[field]));

    const subtotalAmount = hasValue(detail?.subtotal_amount)
      ? roundMoney(detail?.subtotal_amount)
      : itemsSummary.subtotal_amount;

    const discountAmount = hasValue(detail?.discount_amount)
      ? roundMoney(detail?.discount_amount)
      : itemsSummary.discount_amount;

    const taxableAmount = hasValue(detail?.taxable_amount)
      ? roundMoney(detail?.taxable_amount)
      : itemsSummary.taxable_amount;

    const taxAmount = hasValue(detail?.tax_amount)
      ? roundMoney(detail?.tax_amount)
      : itemsSummary.tax_amount;

    const finalAmount = hasValue(detail?.final_amount)
      ? roundMoney(detail?.final_amount)
      : hasValue(detail?.total_amount)
      ? roundMoney(detail?.total_amount)
      : itemsSummary.final_amount;

    const hasData =
      hasStoredBreakdown ||
      subtotalAmount > 0 ||
      discountAmount > 0 ||
      taxableAmount > 0 ||
      taxAmount > 0 ||
      finalAmount > 0;

    return {
      accepted_quantity: itemsSummary.accepted_quantity,
      subtotal_amount: subtotalAmount,
      discount_amount: discountAmount,
      taxable_amount: taxableAmount,
      tax_amount: taxAmount,
      final_amount: finalAmount,
      source: hasStoredBreakdown ? 'saved' : 'computed',
      has_data: hasData,
    };
  }, [detail]);

  if (!detail) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Text type="secondary">No challan data available.</Text>
      </div>
    );
  }

  // ── Update Status Handler ──────────────────────────────────────────────
  const handleUpdateStatus = () => {
    updateForm.setFieldsValue({
      delivery_status: detail.delivery_status,
    });
    setIsUpdateModalVisible(true);
  };

  const handleUpdateSubmit = (values) => {
    dispatch(
      updateDeliveryChallanRequest(detail.id, {
        purchase_order_id: detail.purchase_order_id,
        challan_number: detail.challan_number,
        delivery_status: values.delivery_status,
        delivery_date: detail.delivery_date
          ? new Date(detail.delivery_date).toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0],
      })
    );
    // The saga dispatches fetchDeliveryChallansRequest automatially on success.
    setIsUpdateModalVisible(false);
    onClose();
  };

  // ── Items table (for detailed challans that include items) ─────────────
  const hasItems = detail.items && detail.items.length > 0;

  const itemColumns = [
    {
      title: 'Product',
      dataIndex: 'product_name',
      key: 'product_name',
      width: 200,
    },
    {
      title: 'Ordered Qty',
      dataIndex: 'ordered_quantity',
      key: 'ordered_quantity',
      width: 100,
      align: 'center',
    },
    {
      title: 'Delivered',
      dataIndex: 'delivered_quantity',
      key: 'delivered_quantity',
      width: 100,
      align: 'center',
      render: (v) => <strong style={{ color: '#52c41a' }}>{v || '-'}</strong>,
    },
    {
      title: 'Accepted',
      key: 'accepted_quantity',
      width: 100,
      align: 'center',
      render: (_, record) => <strong style={{ color: '#1890ff' }}>{getAcceptedQuantity(record)}</strong>,
    },
    {
      title: 'Rejected',
      dataIndex: 'rejected_quantity',
      key: 'rejected_quantity',
      width: 100,
      align: 'center',
      render: (v) => (
        <span style={{ color: v > 0 ? '#ff4d4f' : undefined }}>{v > 0 ? v : '-'}</span>
      ),
    },
    {
      title: 'Line Amount (Accepted)',
      key: 'line_amount',
      width: 140,
      align: 'right',
      render: (_, record) => {
        const acceptedQuantity = getAcceptedQuantity(record);
        const computed = computeItemFinancials(record, acceptedQuantity);

        return computed.final_amount > 0
          ? <strong style={{ color: '#531dab' }}>{formatCurrency(computed.final_amount)}</strong>
          : '-';
      },
    },
    {
      title: 'Status',
      dataIndex: 'item_status',
      key: 'item_status',
      width: 110,
      render: (status) => getStatusTag(status),
    },
    {
      title: 'Quality Remarks',
      dataIndex: 'quality_remarks',
      key: 'quality_remarks',
      ellipsis: true,
      render: (text) => text || '-',
    },
  ];

  // ── PO associated challans columns ────────────────────────────────────
  const poChallansColumns = [
    {
      title: 'Challan No.',
      dataIndex: 'challan_number',
      key: 'challan_number',
      render: (text) => <strong style={{ color: '#1890ff' }}>{text}</strong>,
    },
    {
      title: 'Delivery Date',
      dataIndex: 'delivery_date',
      key: 'delivery_date',
      render: (date) =>
        date
          ? new Date(date).toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })
          : '-',
    },
    {
      title: 'Status',
      dataIndex: 'delivery_status',
      key: 'delivery_status',
      render: (status) => getStatusTag(status),
    },
  ];

  return (
    <Spin spinning={loading && !detail.id}>
    <div>
      {/* Header */}
      <Card style={{ marginBottom: 16 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <div>
            <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
              {detail.challan_number}
            </Title>
            <Text type="secondary">
              Purchase Order #{detail.purchase_order_id}
            </Text>
          </div>
          <div>{getStatusTag(detail.delivery_status)}</div>
        </div>
      </Card>

      {/* Basic Information */}
      <Descriptions
        bordered
        size="small"
        column={{ xs: 1, sm: 2 }}
        style={{ marginBottom: 16 }}
      >
        <Descriptions.Item label="Vendor">
          {detail.purchase_order?.vendor_name || detail.vendor_name || 'N/A'}
        </Descriptions.Item>
        <Descriptions.Item label="Firm Name">
          {detail.purchase_order?.firm_name || 'N/A'}
        </Descriptions.Item>
        <Descriptions.Item label="Challan Date">
          {detail.challan_date
            ? new Date(detail.challan_date).toLocaleDateString('en-IN')
            : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Delivery Date">
          {detail.delivery_date
            ? new Date(detail.delivery_date).toLocaleDateString('en-IN')
            : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Received By">
          {detail.received_by_name || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Partial Delivery">
          {detail.is_partial_delivery ? (
            <Tag color="warning">Yes — More deliveries expected</Tag>
          ) : (
            <Tag>No</Tag>
          )}
        </Descriptions.Item>
        {detail.verified_by_name && (
          <>
            <Descriptions.Item label="Verified By">
              {detail.verified_by_name}
            </Descriptions.Item>
            <Descriptions.Item label="Verified At">
              {detail.verified_date
                ? new Date(detail.verified_date).toLocaleString('en-IN')
                : '-'}
            </Descriptions.Item>
          </>
        )}
        <Descriptions.Item label="Remarks" span={2}>
          {detail.remarks || '-'}
        </Descriptions.Item>
        {detail.attachment_url && (
          <Descriptions.Item label="Attachment" span={2}>
            <Button
              icon={<DownloadOutlined />}
              href={detail.attachment_url}
              target="_blank"
              size="small"
            >
              View / Download
            </Button>
          </Descriptions.Item>
        )}
      </Descriptions>

      {/* Item-level details (only for detailed challans — loaded via GET /api/delivery-challans/:id) */}
      {hasItems && (
        <>
          <Divider orientation="left">
            <Title level={5} style={{ margin: 0 }}>
              Delivery Items
            </Title>
          </Divider>

          {/* Summary stats */}
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={12} sm={8}>
              <Card size="small">
                <Statistic
                  title="Total Delivered"
                  value={detail.items.reduce(
                    (sum, i) => sum + (i.delivered_quantity || 0),
                    0
                  )}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8}>
              <Card size="small">
                <Statistic
                  title="Total Accepted"
                  value={detail.items.reduce(
                    (sum, i) => sum + getAcceptedQuantity(i),
                    0
                  )}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8}>
              <Card size="small">
                <Statistic
                  title="Total Rejected"
                  value={detail.items.reduce(
                    (sum, i) => sum + (i.rejected_quantity || 0),
                    0
                  )}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Card>
            </Col>
          </Row>

          <Table
            columns={itemColumns}
            dataSource={detail.items}
            rowKey="id"
            pagination={false}
            size="small"
            bordered
          />
        </>
      )}

      {financialSummary.has_data && (
        <>
          <Divider orientation="left">
            <Title level={5} style={{ margin: 0 }}>
              Amount Breakdown
            </Title>
          </Divider>

          <Row gutter={[12, 12]} style={{ marginBottom: 8 }}>
            <Col xs={12} sm={12} md={8}>
              <Card size="small">
                <Statistic
                  title="Subtotal"
                  value={financialSummary.subtotal_amount}
                  precision={2}
                  prefix="₹"
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={8}>
              <Card size="small" style={{ background: '#fff1f0', borderColor: '#ffccc7' }}>
                <Statistic
                  title="Discount (-)"
                  value={financialSummary.discount_amount}
                  precision={2}
                  prefix="₹"
                  valueStyle={{ color: '#cf1322' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={8}>
              <Card size="small">
                <Statistic
                  title="Taxable"
                  value={financialSummary.taxable_amount}
                  precision={2}
                  prefix="₹"
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={8}>
              <Card size="small" style={{ background: '#fff7e6', borderColor: '#ffd591' }}>
                <Statistic
                  title="Tax (+)"
                  value={financialSummary.tax_amount}
                  precision={2}
                  prefix="₹"
                  valueStyle={{ color: '#d46b08' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={8}>
              <Card size="small" style={{ background: '#f0f5ff', borderColor: '#adc6ff' }}>
                <Statistic
                  title="Final Amount"
                  value={financialSummary.final_amount}
                  precision={2}
                  prefix="₹"
                  valueStyle={{ color: '#1677ff', fontWeight: 700 }}
                />
              </Card>
            </Col>
          </Row>

          <Text type="secondary" style={{ fontSize: 12 }}>
            {financialSummary.source === 'saved'
              ? 'Showing saved challan amount breakdown from the delivery challan record.'
              : 'Showing computed amount breakdown from accepted quantities using item discount and tax values.'}
          </Text>
        </>
      )}

      {/* PO Delivery Status — associated challans */}
      {deliveryStatus && deliveryStatus.challans?.length > 0 && (
        <>
          <Divider orientation="left">
            <Title level={5} style={{ margin: 0 }}>
              All Challans for PO #{challan.purchase_order_id}
            </Title>
          </Divider>

          <Descriptions bordered size="small" column={2} style={{ marginBottom: 12 }}>
            <Descriptions.Item label="PO Delivery Status">
              {getStatusTag(deliveryStatus.delivery_status)}
            </Descriptions.Item>
            <Descriptions.Item label="Total Challans">
              {deliveryStatus.total_challans || 0}
            </Descriptions.Item>
            <Descriptions.Item label="Expected Delivery">
              {deliveryStatus.expected_delivery
                ? new Date(deliveryStatus.expected_delivery).toLocaleDateString('en-IN')
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Total Amount">
              {deliveryStatus.total_amount
                ? `₹${parseFloat(deliveryStatus.total_amount).toFixed(2)}`
                : '-'}
            </Descriptions.Item>
          </Descriptions>

          <Table
            columns={poChallansColumns}
            dataSource={deliveryStatus.challans}
            rowKey="id"
            pagination={false}
            size="small"
            bordered
          />
        </>
      )}

      {/* Actions */}
      <Divider />
      <Space style={{ float: 'right' }}>
        {canUpdate && (
          <Button
            type="primary"
            icon={<TruckOutlined />}
            onClick={handleUpdateStatus}
          >
            Update Status
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </Space>

      {/* Update Status Modal */}
      <Modal
        title="Update Delivery Status"
        open={isUpdateModalVisible}
        onCancel={() => setIsUpdateModalVisible(false)}
        footer={null}
        width={480}
      >
        <Form
          form={updateForm}
          layout="vertical"
          onFinish={handleUpdateSubmit}
        >
          <Form.Item
            label="New Delivery Status"
            name="delivery_status"
            rules={[{ required: true, message: 'Please select a status' }]}
          >
            <Select size="large">
              <Option value="pending">Pending</Option>
              <Option value="in_transit">In Transit</Option>
              <Option value="partially_delivered">Partially Delivered</Option>
              <Option value="delivered">Delivered</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ float: 'right' }}>
              <Button onClick={() => setIsUpdateModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" icon={<CheckOutlined />} loading={loading}>
                Update Status
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
    </Spin>
  );
};

export default DeliveryChallanDetail;
